import { Archive, ArrowDownToLine, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CredentialProposalStatus } from "@api/shared-runtime/enums";
import {
  canApplyProviderOffer,
  filterProviderOffers,
} from "@/features/credentials/lib/provider-import-actions";
import { ProviderOfferSummary } from "./provider-offer-summary";
import {
  formatProviderUpdateDate,
  providerUpdateInstant,
} from "../../lib/provider-import-timestamps";
import {
  ProviderOfferAction,
  ProviderOfferFilter,
  type ProviderOfferSelection,
  type ProviderOfferProgress,
} from "./provider-import-types";

const FILTER_OPTIONS = [
  { value: ProviderOfferFilter.Available, label: "Available" },
  { value: ProviderOfferFilter.Dismissed, label: "Dismissed" },
  {
    value: ProviderOfferFilter.All,
    label: "All shared credentials",
  },
];

export function ProviderOfferList({
  offers,
  filter,
  onFilter,
  canImport,
  disabled,
  progress,
  onReview,
  onImport,
  onDismiss,
  onBulk,
}: {
  offers: ProviderOfferSelection[];
  filter: ProviderOfferFilter;
  onFilter: (filter: ProviderOfferFilter) => void;
  canImport: boolean;
  disabled: boolean;
  progress: ProviderOfferProgress | null;
  onReview: (id: string) => void;
  onImport: (offer: ProviderOfferSelection) => void;
  onDismiss: (offer: ProviderOfferSelection) => void;
  onBulk: (action: ProviderOfferAction) => void;
}) {
  const visible = filterProviderOffers({ offers, filter });
  const canDismiss = canImport && filter === ProviderOfferFilter.Available;
  const importCount = visible.filter((offer) =>
    canApplyProviderOffer(offer, ProviderOfferAction.Import),
  ).length;
  const dismissCount = visible.filter((offer) =>
    canApplyProviderOffer(offer, ProviderOfferAction.Dismiss),
  ).length;
  return (
    <div className="space-y-5">
      <div
        className="flex items-stretch gap-1 border-b border-slate-200 sm:gap-4"
        role="group"
        aria-label="Shared credential filters"
      >
        {FILTER_OPTIONS.map(({ value, label }) => (
          <Button
            key={value}
            type="button"
            size="none"
            variant="none"
            className="min-h-12 min-w-0 flex-1 flex-wrap gap-x-1 whitespace-normal rounded-none border-b-2 border-transparent px-2 py-3 text-xs text-slate-600 hover:text-slate-900 aria-pressed:border-primary aria-pressed:text-primary sm:flex-none sm:px-4 sm:text-sm"
            aria-pressed={filter === value}
            disabled={disabled}
            onClick={() => onFilter(value)}
          >
            {label}{" "}
            <span className="text-xs font-normal tabular-nums text-slate-500">
              {filterProviderOffers({ offers, filter: value }).length}
            </span>
          </Button>
        ))}
      </div>
      {canImport && (
        <div
          className="flex flex-wrap justify-end gap-2"
          role="group"
          aria-label="Bulk credential actions"
        >
          <Button
            type="button"
            className="flex-1 gap-2 sm:flex-none"
            disabled={disabled || importCount === 0}
            onClick={() => onBulk(ProviderOfferAction.Import)}
          >
            <ArrowDownToLine className="size-4" aria-hidden="true" />
            Import all ({importCount})
          </Button>
          {canDismiss && (
            <Button
              type="button"
              variant="outline"
              className="flex-1 gap-2 sm:flex-none"
              disabled={disabled || dismissCount === 0}
              onClick={() => onBulk(ProviderOfferAction.Dismiss)}
            >
              <Archive className="size-4" aria-hidden="true" />
              Dismiss all ({dismissCount})
            </Button>
          )}
        </div>
      )}
      {visible.length === 0 && (
        <p className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-600">
          {filter === ProviderOfferFilter.Available
            ? "No pending provider updates."
            : "No shared credentials in this view."}
        </p>
      )}
      <ul className="space-y-3">
        {visible.map((offer) => {
          const updatedAt =
            filter === ProviderOfferFilter.Available
              ? providerUpdateInstant(offer.sourceUpdatedAt)?.toString()
              : undefined;
          const inProgress = progress?.offers.some(
            (item) => item.id === offer.id,
          );
          return (
            <li
              key={offer.id}
              className="flex flex-wrap items-start gap-4 rounded-xl border bg-white p-4"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <h3 className="break-words text-sm font-semibold text-slate-900">
                  {offer.label}
                  {offer.identityLabel && ` · ${offer.identityLabel}`}
                </h3>
                <ProviderOfferSummary offer={offer} stale={false} />
                {updatedAt && (
                  <p className="text-xs text-slate-500">
                    Updated{" "}
                    <time dateTime={updatedAt}>
                      {formatProviderUpdateDate(updatedAt)}
                    </time>
                  </p>
                )}
                {offer.dismissedAt &&
                  offer.status === CredentialProposalStatus.Pending && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">
                      <Archive className="size-3" aria-hidden="true" />
                      Dismissed
                      {offer.dismissedByName && ` · ${offer.dismissedByName}`}
                    </span>
                  )}
              </div>
              <div className="flex w-full shrink-0 flex-wrap justify-end gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => onReview(offer.id)}
                >
                  Review
                </Button>
                {canImport && offer.isActionable && (
                  <Button
                    type="button"
                    size="sm"
                    className="gap-2"
                    disabled={
                      disabled ||
                      !canApplyProviderOffer(offer, ProviderOfferAction.Import)
                    }
                    aria-label={`Import ${offer.label}${offer.identityLabel ? ` ${offer.identityLabel}` : ""}`}
                    onClick={() => onImport(offer)}
                  >
                    {inProgress &&
                    progress?.action === ProviderOfferAction.Import ? (
                      <LoaderCircle
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <ArrowDownToLine className="size-4" aria-hidden="true" />
                    )}
                    Import
                  </Button>
                )}
                {canDismiss &&
                  canApplyProviderOffer(offer, ProviderOfferAction.Dismiss) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-2 text-slate-600"
                      disabled={disabled}
                      aria-label={`Dismiss ${offer.label}${offer.identityLabel ? ` ${offer.identityLabel}` : ""}`}
                      onClick={() => onDismiss(offer)}
                    >
                      {inProgress &&
                      progress?.action === ProviderOfferAction.Dismiss ? (
                        <LoaderCircle
                          className="size-4 animate-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <Archive className="size-4" aria-hidden="true" />
                      )}
                      Dismiss
                    </Button>
                  )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
